import { Heading, Text } from "@/design-system";
import { useScrollDepth } from "@/hooks";
import { trackEvent } from "@/lib/dataLayer";
import {
  CookieConsent,
  DemoFormModal,
  FAQ,
  Footer,
  Header,
  WhatsAppFloatButton,
  type FAQItem,
} from "@/pages/landingPage/components";
import { type ReactNode, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router";
import { type Crumb, SITE_URL, breadcrumbSchema } from "../constants";

type ToolPageLayoutProps = {
  /** Sem "| Fidexa": o titleTemplate de src/App.tsx acrescenta. */
  title: string;
  description: string;
  /** Trilha depois de "Início"; o último item é a própria página. */
  trail: Crumb[];
  /** JSON-LD da página além do BreadcrumbList (o FAQPage sai do FAQ). */
  schemas: object[];
  eyebrow: string;
  heading: string;
  lead: ReactNode;
  faqItems: FAQItem[];
  faqHeading: string;
  /**
   * Corpo da ferramenta. Recebe quem abre o formulário de demonstração, para o
   * convite abaixo do resultado.
   */
  children: (openDemo: () => void) => ReactNode;
};

/**
 * Casca comum das páginas de /ferramentas: head, header, hero, FAQ e rodapé.
 *
 * Tudo que não é a ferramenta leva `print:hidden` — no gerador de carnê o
 * "Imprimir" tem de sair só com o carnê na folha.
 */
export const ToolPageLayout = ({
  title,
  description,
  trail,
  schemas,
  eyebrow,
  heading,
  lead,
  faqItems,
  faqHeading,
  children,
}: ToolPageLayoutProps) => {
  useScrollDepth();
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const current = trail[trail.length - 1];
  const url = `${SITE_URL}${current.path}`;
  const ogTitle = `${title} | Fidexa`;

  const openDemo = (location: "header" | "tool_result") => {
    trackEvent({ event: "cta_click_specialist", location });
    setIsDemoOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fidexa" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema(trail))}
        </script>
        {schemas.map(schema => (
          <script
            key={JSON.stringify(schema).slice(0, 80)}
            type="application/ld+json"
          >
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      <div className="bg-ds-paper min-h-screen print:bg-transparent">
        <div className="print:hidden">
          <Header onSpecialistClick={() => openDemo("header")} />
        </div>
        <main className="flex flex-col gap-16 pb-16 md:gap-20 print:block print:p-0">
          <section
            aria-labelledby="hero-title"
            className="mx-auto flex w-full max-w-6xl flex-col items-start gap-4 px-4 pt-28 sm:px-6 md:pt-36 lg:px-8 print:hidden"
          >
            <nav aria-label="Trilha de navegação">
              <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {[{ name: "Início", path: "/" }, ...trail].map(
                  (crumb, index, all) => {
                    const isLast = index === all.length - 1;
                    return (
                      <li key={crumb.path} className="flex items-center gap-2">
                        {isLast ? (
                          <Text
                            as="span"
                            size="small"
                            tone="subtle"
                            aria-current="page"
                          >
                            {crumb.name}
                          </Text>
                        ) : (
                          <>
                            <Link
                              to={crumb.path}
                              className="text-ds-small text-ds-brand-700 hover:text-ds-brand-800 focus-visible:outline-ds-focus inline-flex min-h-11 min-w-11 cursor-pointer items-center underline-offset-4 hover:underline focus-visible:outline-2"
                            >
                              {crumb.name}
                            </Link>
                            <Text
                              as="span"
                              size="small"
                              tone="subtle"
                              aria-hidden="true"
                            >
                              /
                            </Text>
                          </>
                        )}
                      </li>
                    );
                  }
                )}
              </ol>
            </nav>
            <Text size="small" tone="brand" weight="semibold">
              {eyebrow}
            </Text>
            <Heading
              level={1}
              id="hero-title"
              className="md:text-ds-display max-w-3xl"
            >
              {heading}
            </Heading>
            <Text size="lead" tone="muted" className="max-w-2xl">
              {lead}
            </Text>
          </section>

          {children(() => openDemo("tool_result"))}

          <section className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 print:hidden">
            <FAQ
              items={faqItems}
              headingOverride={faqHeading}
              headingClassName="text-ds-h2 sm:text-ds-h1 text-left"
            />
          </section>
        </main>
        <div className="print:hidden">
          <Footer />
          <WhatsAppFloatButton />
          <CookieConsent />
        </div>
        <DemoFormModal
          isOpen={isDemoOpen}
          onClose={() => setIsDemoOpen(false)}
        />
      </div>
    </>
  );
};
